/**
 * Exposures toggle row — Vol Timing / Constraints / Transaction Costs.
 *
 * Writes the scenario flags into the MARS portfolio store; the chart
 * builders read those flags to choose which scenario series to draw.
 * Funds expose different toggles:
 *   - `mars-qmas`   — no toggles at all (component renders null).
 *   - Constraints   — shown for DAR-NN only.
 *   - Turning Vol Timing OFF forces Transaction Costs off (and, for
 *     DAR-NN, Constraints on) — the no-vol-timing scenario has no TC
 *     variant, so leaving TC on would request a series that doesn't exist.
 *
 * `inline` tightens the spacing when the row is embedded in another
 * controls row (see ExposuresSection).
 */
import { useCallback, useState } from 'react';
import { useMarsPortfolioStore } from '../store';
import { trackActivity } from '@/lib/analytics/tracker';
import type { Fund } from '../types';

const TOGGLE_TOOLTIPS: Record<string, string> = {
  'Vol Timing':
    'Turning off Vol Timing reveals the portfolio of an agent who adjusts positions only in response to changes in expected returns, holding asset volatilities fixed. Comparing this to the full portfolio allows you to separate how much of the allocation change is driven by return forecasts versus volatility dynamics.',
  'Transaction Costs':
    'Turning off Transaction Cost shows the portfolio of an agent who does not incur any transaction costs. Comparing this to the full portfolio helps identify how much of the allocation change is driven by transaction-cost considerations versus changes in investment opportunities.',
};

interface AssetTogglesProps {
  fund: Fund | undefined;
  inline?: boolean;
}

export default function AssetToggles({
  fund,
  inline = false,
}: AssetTogglesProps): JSX.Element | null {
  const volTiming = useMarsPortfolioStore((s) => s.volTiming);
  const constraints = useMarsPortfolioStore((s) => s.constraints);
  const tc = useMarsPortfolioStore((s) => s.tc);
  const setVolTiming = useMarsPortfolioStore((s) => s.setVolTiming);
  const setConstraints = useMarsPortfolioStore((s) => s.setConstraints);
  const setTc = useMarsPortfolioStore((s) => s.setTc);

  const isDarNn = fund?.id === 'mars-dar-nn';

  // Toggle changes are tracked as synthetic page_views (matches the legacy
  // mars-portfolio-dashboard.js tracking). Only the directly-clicked toggle
  // is recorded, not the cascaded side-effects.
  const trackToggle = useCallback(
    (label: string, value: boolean) => {
      const name = fund?.display_name ?? fund?.id ?? 'Portfolio';
      trackActivity('page_view', `Portfolio: ${name} - ${label} ${value ? 'On' : 'Off'}`, {});
    },
    [fund],
  );

  const handleVolTimingChange = useCallback(
    (value: boolean) => {
      setVolTiming(value);
      trackToggle('Vol Timing', value);
      if (!value) {
        setTc(false);
        if (isDarNn) setConstraints(true);
      }
    },
    [setVolTiming, setTc, setConstraints, isDarNn, trackToggle],
  );

  const handleConstraintsChange = useCallback(
    (value: boolean) => {
      setConstraints(value);
      trackToggle('Constraints', value);
    },
    [setConstraints, trackToggle],
  );

  const handleTcChange = useCallback(
    (value: boolean) => {
      setTc(value);
      trackToggle('Transaction Costs', value);
    },
    [setTc, trackToggle],
  );

  if (!fund || fund.id === 'mars-qmas') return null;

  const showConstraints = fund.id === 'mars-dar-nn';
  const tcDisabled = !volTiming;
  const constraintsDisabled = isDarNn && !volTiming;

  const wrapperClass = inline
    ? 'flex items-center gap-3 flex-wrap'
    : 'flex items-center gap-4 flex-wrap';

  return (
    <div className={wrapperClass}>
      <span className="text-sm font-semibold text-[color:var(--text-dark)]">Exposures:</span>
      <div className="flex items-center gap-3 flex-wrap">
        <Toggle
          label="Vol Timing"
          checked={volTiming}
          onChange={handleVolTimingChange}
          tooltip={TOGGLE_TOOLTIPS['Vol Timing']}
        />
        {showConstraints ? (
          <Toggle
            label="Constraints"
            checked={constraints}
            onChange={handleConstraintsChange}
            disabled={constraintsDisabled}
          />
        ) : null}
        <Toggle
          label="Transaction Costs"
          checked={tc}
          onChange={handleTcChange}
          disabled={tcDisabled}
          tooltip={TOGGLE_TOOLTIPS['Transaction Costs']}
        />
      </div>
    </div>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
}

function Toggle({ label, checked, onChange, disabled = false, tooltip }: ToggleProps): JSX.Element {
  const [showTip, setShowTip] = useState(false);
  return (
    <span className={`relative flex items-center gap-2 ${disabled ? 'opacity-50' : ''}`}>
      <label
        className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="relative inline-block w-11 h-6">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className="absolute inset-0 bg-gray-200 rounded-full peer-checked:bg-[color:var(--accent)] transition-colors" />
          <span
            className={`absolute top-[2px] left-[2px] h-5 w-5 bg-white border border-gray-300 rounded-full transition-transform ${
              checked ? 'translate-x-5' : ''
            }`}
          />
        </span>
        <span className="text-sm text-[color:var(--text-dark)]">{label}</span>
      </label>
      {tooltip ? (
        <>
          <button
            type="button"
            onClick={() => setShowTip((v) => !v)}
            className="inline-flex items-center justify-center min-w-[1.5rem] min-h-[1.5rem] rounded-full bg-[#f8f9fa] border border-[#e9ecef] text-[color:var(--accent)] text-base font-bold cursor-pointer transition-all hover:bg-[color:var(--accent)] hover:text-white hover:shadow-[0_2px_8px_rgba(78,183,172,0.3)] hover:scale-110"
            title="Click for more information"
          >
            ⓘ
          </button>
          {showTip ? (
            <div className="absolute top-full left-0 z-50 mt-2 w-72 rounded-lg border border-gray-200 bg-white p-3 text-sm text-[color:var(--text-dark)] shadow-lg">
              <strong>{label}</strong>
              <br />
              {tooltip}
            </div>
          ) : null}
        </>
      ) : null}
    </span>
  );
}
